import { gpCommon } from '../app';

export interface IStorageService {
    getAsString(key: string): string | null;
    setAsString(key: string, value: string): void;

    getAsObject<T>(key: string): T | null;
    setAsObject<T>(key: string, value: T): void;

    remove(key: string): void;
}

export interface IScopedStorageOptions {
    useUserDefaults?: boolean;
    usePlanDefaults?: boolean;
}

export class ScopedStorage<T> {
    planSpecificKey: string;
    userDefaultsKey: string;
    constructor(private planScope: string, private applicationScope: string, private getDefaults: () => T, private storage: IStorageService,) {
        this.planSpecificKey = `${this.planScope}/${this.applicationScope}/values`;
        this.userDefaultsKey = `__user_default__/${this.applicationScope}/values`;
    }

    public save(values: Partial<T>, options?: IScopedStorageOptions) {
        let saveOptions: IScopedStorageOptions = mergeScopedStorageOptions(options);
        let oldValues = this.load();
        let newValues = { ...oldValues, ...values };

        if (saveOptions.useUserDefaults) {
            this.storage.setAsObject(this.userDefaultsKey, newValues);
        }

        if (saveOptions.usePlanDefaults) {
            this.storage.setAsObject(this.planSpecificKey, newValues);
        }
    }

    public load(customCombining?: (systemDefault: Partial<T>, userDefaultSettings: Partial<T>, planSettings: Partial<T>, resultSettings: T) => void, options?: IScopedStorageOptions): T {
        let loadOptions: IScopedStorageOptions = mergeScopedStorageOptions(options);
        const systemDefaultsSettings = this.getDefaults();
        const userDefaultSettings = (loadOptions.useUserDefaults ? this.storage.getAsObject<Partial<T>>(this.userDefaultsKey) : null) || {};
        const planSettings = (loadOptions.usePlanDefaults ? this.storage.getAsObject<Partial<T>>(this.planSpecificKey) : null) || {};

        let result = { ...systemDefaultsSettings, ...userDefaultSettings, ...planSettings };

        if (customCombining) {
            customCombining(systemDefaultsSettings, userDefaultSettings, planSettings, result)
        }

        return result;
    }
}

export interface ISessionStorageService {
    session_getAsString(key: string): string | null;
    session_setAsString(key: string, value: string): void;

    session_getAsObject<T>(key: string): T | null;
    session_setAsObject<T>(key: string, value: T): void;

    session_remove(key: string): void;
}

class StorageService implements IStorageService {

    public constructor(protected storage: Storage) {
    }

    getAsString(key: string) {
        return this.storage.getItem(key);
    }

    setAsString(key: string, value: string): void {
        this.storage.setItem(key, value);
    }

    getAsObject<T>(key: string): T | null {

        var json = this.getAsString(key);

        if (json === null) return null;
        var obj = JSON.parse(json);
        return obj;
    }

    setAsObject<T>(key: string, obj: T): void {
        var json = JSON.stringify(obj);
        this.setAsString(key, json);
    }

    remove(key: string): void {
        this.storage.removeItem(key);
    }
}

class SessionStorageService implements ISessionStorageService {

    public constructor(protected storage: Storage) {
    }

    session_getAsString(key: string): string | null {
        return this.storage.getItem(key);
    }

    session_setAsString(key: string, value: string): void {
        this.storage.setItem(key, value);
    }

    session_getAsObject<T>(key: string): T | null {

        var json = this.session_getAsString(key);

        if (json === null) return null;
        try {
            var obj = JSON.parse(json);
            return obj;
        } catch {
            return null;
        }
    }

    session_setAsObject<T>(key: string, obj: T): void {
        var json = JSON.stringify(obj);
        this.session_setAsString(key, json);
    }

    session_remove(key: string): void {
        this.storage.removeItem(key);
    }
}

class StorageWithVersionNumber extends StorageService {

    private current_storage_version = "v2";
    private storage_version_key = "gameplan.storage.version";

    constructor(storage: Storage) {
        super(storage);

        if (this.getAsString(this.storage_version_key) !== this.current_storage_version) {
            this.storage.clear();
            this.setAsString(this.storage_version_key, this.current_storage_version);
        }
    }

}


class SessionStorageWithVersionNumber extends SessionStorageService {

    private current_storage_version = "v3";
    private storage_version_key = "gameplan.storage.version";

    constructor(storage: Storage) {
        super(storage);

        if (this.session_getAsString(this.storage_version_key) !== this.current_storage_version) {
            this.storage.clear();
            this.session_setAsString(this.storage_version_key, this.current_storage_version);
        }
    }

}

function mergeScopedStorageOptions(options: IScopedStorageOptions | undefined): IScopedStorageOptions {
    return {
        usePlanDefaults: true,
        useUserDefaults: true,
        ...(options || {}),
    };
}

export function gpLocalStorageServiceFactory(): IStorageService {
    return new StorageWithVersionNumber(localStorage);
}

declare module 'angular' {
  namespace auto {
    interface IInjectorService {
      get(name: 'gpLocalStorageService'): IStorageService;
      get(name: 'gpSessionStorageService'): ISessionStorageService;
    }
  }
}


gpCommon.factory('gpLocalStorageService', [function (): IStorageService {
    return new StorageWithVersionNumber(localStorage);
}]);

gpCommon.factory('gpSessionStorageService', [function (): ISessionStorageService {
    return new SessionStorageWithVersionNumber(sessionStorage);
}]);
